import { Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { API } from 'src/environments/environment';
import { FIN_SANTANDER_ID_EMPRESA_CONFIG } from 'src/app/constants/santander-financeiro.constants';
import { ObservacaoContaReceberPayload } from 'src/app/service/fin-conta-receber-lista.service';
import { AjaxService } from './ajax.service';
import { UtilService } from './util.service';

export interface SantanderRegistroDiagnostico {
  idEmpresa: number;
  codigoBanco: string;
  descricao?: string;
  temClientId?: boolean;
  temWorkspace?: boolean;
  temCertificado?: boolean;
  apiClientId?: string;
  codigoConvenio?: string;
  certificadoArquivo?: string;
  apiAmbiente?: string;
  workspaceId?: string;
  webhookUrl?: string;
}

export interface SantanderApiConfigView {
  configurado: boolean;
  descricao: string;
  agencia: string;
  digitoAgencia: string;
  contaCorrente: string;
  digitoConta: string;
  nomeBeneficiarioCobranca: string;
  apiClientId: string;
  clientSecretConfigurado: boolean;
  apiAmbiente: 'S' | 'P';
  workspaceId: string;
  codigoConvenio: string;
  certificadoConfigurado: boolean;
  certificadoArquivo: string;
  webhookUrl: string;
  webhookBoletoAtivo: boolean;
  webhookPixAtivo: boolean;
  webhookUrlSugerida: string;
  apiMultaPercentual?: number | null;
  apiJurosPercentualMensal?: number | null;
  apiMultaDiasAposVencimento?: number | null;
  registrosNoBanco?: SantanderRegistroDiagnostico[];
}

export interface SantanderWebhookLogItem {
  id: number;
  idContaReceber: number | null;
  funcao: string;
  paymentType: string;
  bankNumber: string;
  clientNumber: string;
  processado: boolean;
  liquidacaoAplicada: boolean;
  estornoAplicado: boolean;
  mensagem: string;
  dataHora: string;
}

export interface SantanderConsultaTituloResultado {
  idContaReceber: number;
  statusSantander: string;
  liquidado: boolean;
  atualizado: boolean;
  mensagem: string;
}

export interface SalvarSantanderApiConfigPayload {
  descricao: string;
  agencia: string;
  digitoAgencia: string;
  contaCorrente: string;
  digitoConta: string;
  nomeBeneficiarioCobranca: string;
  apiClientId: string;
  apiClientSecret: string;
  apiAmbiente: 'S' | 'P';
  codigoConvenio: string;
  webhookUrl?: string;
  webhookBoletoAtivo?: boolean;
  webhookPixAtivo?: boolean;
  apiMultaPercentual?: number | null;
  apiJurosPercentualMensal?: number | null;
  apiMultaDiasAposVencimento?: number | null;
}

@Injectable({
  providedIn: 'root'
})
export class FinSantanderApiService {
  constructor(
    private ajax: AjaxService,
    private util: UtilService
  ) {}

  obterConfig(): Observable<SantanderApiConfigView> {
    return this.ajax.get(`${API}financeiro/SantanderApi/Config`).pipe(
      map((r: any) => this.mapearConfigView(r))
    );
  }

  private mapearConfigView(r: any): SantanderApiConfigView {
    const base = this.vazio();
    if (!r) {
      return base;
    }
    if (r?.hasErrors && r?.data == null) {
      return base;
    }

    const d = this.desempacotar(r);
    const temDados =
      this.util.parseBoolean(d.configurado) ||
      Boolean(
        d.agencia ||
          d.apiclientid ||
          d.apiClientId ||
          d.codigoconvenio ||
          d.codigoConvenio ||
          d.workspaceid ||
          d.workspaceId ||
          d.certificadoarquivo ||
          d.certificadoArquivo
      );

    const registros = Array.isArray(d.registrosnobanco)
      ? d.registrosnobanco
      : Array.isArray(d.registrosNoBanco)
        ? d.registrosNoBanco
        : [];

    if (!temDados) {
      const registrosMapeados = this.mapearRegistrosDiagnostico(registros);
      const santanderDiag = this.resolverRegistroSantanderDiagnostico(registrosMapeados);
      if (santanderDiag) {
        const ambDiag = String(santanderDiag.apiAmbiente ?? 'S').toUpperCase();
        return {
          ...base,
          configurado: true,
          descricao: santanderDiag.descricao || base.descricao,
          apiClientId: String(santanderDiag.apiClientId ?? ''),
          codigoConvenio: String(santanderDiag.codigoConvenio ?? ''),
          workspaceId: santanderDiag.workspaceId || '',
          webhookUrl: santanderDiag.webhookUrl || '',
          webhookUrlSugerida: String(d.webhookurlsugerida ?? d.webhookUrlSugerida ?? ''),
          clientSecretConfigurado: Boolean(santanderDiag.temClientId),
          certificadoConfigurado: Boolean(santanderDiag.temCertificado),
          certificadoArquivo: String(santanderDiag.certificadoArquivo ?? ''),
          apiAmbiente: ambDiag === 'P' ? 'P' : 'S',
          registrosNoBanco: registrosMapeados,
        };
      }
      if (registros.length) {
        console.warn('Santander API: nenhuma config 033 encontrada. Registros em FIN_COBRANCA_BANCO:', registros);
      }
      return {
        ...base,
        webhookUrlSugerida: String(d.webhookurlsugerida ?? d.webhookUrlSugerida ?? ''),
        registrosNoBanco: registrosMapeados,
      };
    }

    const amb = String(d.apiambiente ?? d.apiAmbiente ?? 'S').toUpperCase();
    return {
      configurado: true,
      descricao: String(d.descricao ?? base.descricao),
      agencia: String(d.agencia ?? ''),
      digitoAgencia: String(d.digitoagencia ?? d.digitoAgencia ?? ''),
      contaCorrente: String(d.contacorrente ?? d.contaCorrente ?? ''),
      digitoConta: String(d.digitoconta ?? d.digitoConta ?? ''),
      nomeBeneficiarioCobranca: String(d.nomebeneficiariocobranca ?? d.nomeBeneficiarioCobranca ?? ''),
      apiClientId: String(d.apiclientid ?? d.apiClientId ?? ''),
      clientSecretConfigurado: this.util.parseBoolean(d.clientsecretconfigurado ?? d.clientSecretConfigurado),
      apiAmbiente: amb === 'P' ? 'P' : 'S',
      workspaceId: String(d.workspaceid ?? d.workspaceId ?? ''),
      codigoConvenio: String(d.codigoconvenio ?? d.codigoConvenio ?? ''),
      certificadoConfigurado: this.util.parseBoolean(d.certificadoconfigurado ?? d.certificadoConfigurado),
      certificadoArquivo: String(d.certificadoarquivo ?? d.certificadoArquivo ?? ''),
      webhookUrl: String(d.webhookurl ?? d.webhookUrl ?? ''),
      webhookBoletoAtivo: this.util.parseBoolean(d.webhookboletoativo ?? d.webhookBoletoAtivo ?? true),
      webhookPixAtivo: this.util.parseBoolean(d.webhookpixativo ?? d.webhookPixAtivo ?? true),
      webhookUrlSugerida: String(d.webhookurlsugerida ?? d.webhookUrlSugerida ?? ''),
      apiMultaPercentual: this.parseNumeroOpcional(d.apimultapercentual ?? d.apiMultaPercentual),
      apiJurosPercentualMensal: this.parseNumeroOpcional(d.apijurospercentualmensal ?? d.apiJurosPercentualMensal),
      apiMultaDiasAposVencimento: this.parseInteiroOpcional(d.apimultadiasaposvencimento ?? d.apiMultaDiasAposVencimento),
    };
  }

  salvarConfig(payload: SalvarSantanderApiConfigPayload): Observable<any> {
    return this.ajax.post(`${API}financeiro/SantanderApi/Config`, payload);
  }

  uploadCertificado(arquivo: File, senha: string): Observable<any> {
    const fd = new FormData();
    fd.append('arquivo', arquivo, arquivo.name);
    fd.append('senha', senha || '');
    return this.ajax.postFile(`${API}financeiro/SantanderApi/UploadCertificado`, fd);
  }

  testarConexao(): Observable<any> {
    return this.ajax.post(`${API}financeiro/SantanderApi/TestarConexao`, {});
  }

  criarWorkspace(): Observable<any> {
    return this.ajax.post(`${API}financeiro/SantanderApi/CriarWorkspace`, {});
  }

  configurarWebhook(): Observable<any> {
    return this.ajax.post(`${API}financeiro/SantanderApi/ConfigurarWebhook`, {});
  }

  consultarBoletos(ids: number[]): Observable<SantanderConsultaTituloResultado[]> {
    return this.ajax.post(`${API}financeiro/SantanderApi/ConsultarBoletos`, { ids }).pipe(
      map((r: any) => {
        if (r?.hasErrors) {
          throw new Error(r.message || 'Falha na consulta.');
        }
        const d = this.desempacotar(r);
        const titulos = d.titulos || d.Titulos || [];
        return (Array.isArray(titulos) ? titulos : []).map((t: any) => ({
          idContaReceber: Number(t.idcontareceber ?? t.idContaReceber ?? 0),
          statusSantander: String(t.statussantander ?? t.statusSantander ?? ''),
          liquidado: Boolean(t.liquidado),
          atualizado: Boolean(t.atualizado),
          mensagem: String(t.mensagem ?? ''),
        }));
      })
    );
  }

  listarWebhookLogs(limite = 30): Observable<SantanderWebhookLogItem[]> {
    return this.ajax.get(`${API}financeiro/SantanderApi/WebhookLogs?limite=${limite}`).pipe(
      map((r: any) => {
        if (r?.hasErrors) {
          return [];
        }
        const d = this.desempacotar(r);
        const logs = d.logs || d.Logs || [];
        return (Array.isArray(logs) ? logs : []).map((x: any) => ({
          id: Number(x.id ?? 0),
          idContaReceber: x.idcontareceber != null ? Number(x.idcontareceber) : null,
          funcao: String(x.funcao ?? ''),
          paymentType: String(x.paymenttype ?? x.paymentType ?? ''),
          bankNumber: String(x.banknumber ?? x.bankNumber ?? ''),
          clientNumber: String(x.clientnumber ?? x.clientNumber ?? ''),
          processado: Boolean(x.processado),
          liquidacaoAplicada: Boolean(x.liquidacaoaplicada ?? x.liquidacaoAplicada),
          estornoAplicado: Boolean(x.estornoaplicado ?? x.estornoAplicado),
          mensagem: String(x.mensagem ?? ''),
          dataHora: String(x.datahora ?? x.dataHora ?? ''),
        }));
      })
    );
  }

  /** Registra boletos na API Santander e abre HTML para impressão. */
  registrarBoletosAbrirGuia(ids: number[], observacoes?: ObservacaoContaReceberPayload[]): Observable<void> {
    return this.ajax
      .postBlob(`${API}financeiro/SantanderApi/RegistrarBoletosHtml`, { ids, observacoes: observacoes ?? [] })
      .pipe(switchMap((resp) => from(this.tratarBoletoHtml(resp))));
  }

  /** Abre HTML do novo modelo V2 (mock) — não registra no banco. */
  previewBoletoV2MockAbrirGuia(): Observable<void> {
    return this.ajax
      .getBlob(`${API}financeiro/SantanderApi/PreviewBoletoV2Mock`)
      .pipe(switchMap((resp) => from(this.tratarBoletoHtml(resp))));
  }

  private async tratarBoletoHtml(resp: HttpResponse<Blob>): Promise<void> {
    const ct = (resp.headers.get('content-type') || '').toLowerCase();
    const blob = resp.body;
    if (!blob) {
      throw new Error('Resposta vazia do servidor.');
    }
    if (ct.includes('application/json')) {
      const texto = await this.blobParaTexto(blob);
      let j: any;
      try {
        j = JSON.parse(texto);
      } catch {
        throw new Error('Resposta inválida do servidor.');
      }
      if (j.hasErrors) {
        throw new Error(j.message || 'Não foi possível registrar o boleto.');
      }
      return;
    }

    const url = URL.createObjectURL(blob);
    const janela = window.open(url, '_blank', 'noopener');
    if (!janela) {
      URL.revokeObjectURL(url);
      throw new Error('O navegador bloqueou a nova guia. Permita pop-ups para este site e tente de novo.');
    }
    setTimeout(() => URL.revokeObjectURL(url), 120000);
  }

  private blobParaTexto(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result ?? ''));
      fr.onerror = () => reject(fr.error ?? new Error('Falha ao ler resposta.'));
      fr.readAsText(blob);
    });
  }

  private mapearRegistrosDiagnostico(registros: any[]): SantanderRegistroDiagnostico[] {
    return (registros || []).map((r: any) => ({
      idEmpresa: Number(r.idempresa ?? r.idEmpresa ?? 0),
      codigoBanco: String(r.codigobanco ?? r.codigoBanco ?? ''),
      descricao: String(r.descricao ?? ''),
      temClientId: Boolean(r.temclientid ?? r.temClientId),
      temWorkspace: Boolean(r.temworkspace ?? r.temWorkspace),
      temCertificado: Boolean(r.temcertificado ?? r.temCertificado),
      apiClientId: String(r.apiclientid ?? r.apiClientId ?? ''),
      codigoConvenio: String(r.codigoconvenio ?? r.codigoConvenio ?? ''),
      certificadoArquivo: String(r.certificadoarquivo ?? r.certificadoArquivo ?? ''),
      apiAmbiente: String(r.apiambiente ?? r.apiAmbiente ?? ''),
      workspaceId: String(r.workspaceid ?? r.workspaceId ?? ''),
      webhookUrl: String(r.webhookurl ?? r.webhookUrl ?? ''),
    }));
  }

  /** Preferência: registro 033 Santander na empresa de configuração (1). */
  private resolverRegistroSantanderDiagnostico(
    registros: SantanderRegistroDiagnostico[]
  ): SantanderRegistroDiagnostico | null {
    if (!registros?.length) {
      return null;
    }
    const empresaConfig = FIN_SANTANDER_ID_EMPRESA_CONFIG;
    const porEmpresa = registros.filter((r) => r.idEmpresa === empresaConfig);
    const candidatos = porEmpresa.length ? porEmpresa : registros;
    return (
      candidatos.find((r) => r.codigoBanco === '033' && (r.apiClientId || r.temWorkspace || r.temClientId || r.temCertificado)) ||
      candidatos.find((r) => r.apiClientId || r.temWorkspace || r.temClientId || r.temCertificado) ||
      candidatos.find((r) => r.codigoBanco === '033') ||
      null
    );
  }

  private vazio(): SantanderApiConfigView {
    return {
      configurado: false,
      descricao: 'Banco Santander S.A.',
      agencia: '',
      digitoAgencia: '',
      contaCorrente: '',
      digitoConta: '',
      nomeBeneficiarioCobranca: '',
      apiClientId: '',
      clientSecretConfigurado: false,
      apiAmbiente: 'S',
      workspaceId: '',
      codigoConvenio: '',
      certificadoConfigurado: false,
      certificadoArquivo: '',
      webhookUrl: '',
      webhookBoletoAtivo: true,
      webhookPixAtivo: true,
      webhookUrlSugerida: '',
      apiMultaPercentual: null,
      apiJurosPercentualMensal: null,
      apiMultaDiasAposVencimento: null,
    };
  }

  private parseNumeroOpcional(valor: unknown): number | null {
    if (valor == null || valor === '') {
      return null;
    }
    const n = Number(valor);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  private parseInteiroOpcional(valor: unknown): number | null {
    if (valor == null || valor === '') {
      return null;
    }
    const n = parseInt(String(valor), 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }

  private desempacotar(response: any): any {
    let atual = response;
    for (let i = 0; i < 3; i++) {
      if (atual && typeof atual === 'object' && Object.prototype.hasOwnProperty.call(atual, 'data') && atual.data != null) {
        atual = atual.data;
      } else {
        break;
      }
    }
    return this.util.converterParaLowercase(atual ?? {});
  }
}
